import React, {useState} from 'react';
import {graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {Button} from '@/web-components';
import Util from '../../forecast-app/shared/util/util';
import BackfillClientContacts from '../../mutations/mission_control/backfill_client_contacts';
import {createToast} from '../../forecast-app/shared/components/toasts/toast';
import MissionControlHeader from './type-script-files/MissionControlHeader';
import {Alert, FlexColumn, Icon, Label, SubHeading, Table, Text, Tooltip} from '@forecast-it/design-system';
import {companyMeta, noBackfillNote, toCompanyGroups} from './clientContactsBackfillReport';
import {
	CenteredCell,
	ColumnBar,
	CompaniesPanel,
	CompanyItem,
	CompanyRowHeader,
	GuestBox,
	GuestPanel,
	PanelHeader,
	FullValueText,
	HoverText,
	RightCell,
} from './clientContactsBackfill.styled';

// The company service handles one bounded chunk per call, so the page walks the estate itself
// rather than holding a single request open across every company.
const CHUNK_SIZE = 25;
const NOT_AUTHORIZED = {notAuthorized: true};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Cell text that shows the whole value while hovered: the full name or client name, the email or id
// under it, and how long any shortened value really is. The tooltip can be hovered too, so an email
// in it can be selected and copied.
const HoverValue = ({tooltip, children}) => (
	<Tooltip
		width={Tooltip.WIDTH.l}
		content={
			<FullValueText>
				<FlexColumn gap="xs">
					<SubHeading>{tooltip.title}</SubHeading>
					<Text>{tooltip.details}</Text>
					{tooltip.note && (
						<Text size="2" color="medium">
							{tooltip.note}
						</Text>
					)}
				</FlexColumn>
			</FullValueText>
		}
	>
		<HoverText data-cy="backfill-hover-value">{children}</HoverText>
	</Tooltip>
);

// One company with contacts to create. Clicking the line, or pressing Enter or Space on it, shows
// the guests it would create. The guest table only mounts while open, so a very large estate does
// not render every guest at once.
const CompanyRow = ({group, open, onToggle}) => (
	<CompanyItem>
		<CompanyRowHeader
			open={open}
			role="button"
			tabIndex={0}
			aria-expanded={open}
			onClick={onToggle}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onToggle();
				}
			}}
			data-cy={`backfill-company-${group.companyId}`}
		>
			<CenteredCell>
				<Icon icon={open ? 'chevronDown' : 'chevronRight'} size="m" />
			</CenteredCell>
			<FlexColumn gap="xs">
				<Label>{group.companyName}</Label>
				<Text color="medium">{group.meta}</Text>
			</FlexColumn>
			<RightCell>
				<Label>{group.contactsToCreate}</Label>
			</RightCell>
		</CompanyRowHeader>
		{open && (
			<GuestPanel>
				{group.guests.length < group.contactsToCreate && (
					<Text color="medium" data-cy="backfill-guests-capped">
						Showing the first {group.guests.length} of {group.contactsToCreate} guests for this company.
					</Text>
				)}
				<GuestBox>
					<Table data={group.guests} width="100%" getRowId={row => row.id} data-cy="backfill-guest-table">
						<Table.TextColumn accessorKey="personId" header="Person ID" width="10%" />
						<Table.TitleSubtitleColumn
							accessorKey="guestName"
							header="Guest"
							renderTitle={row => <HoverValue tooltip={row.guestTooltip}>{row.guestName}</HoverValue>}
							renderSubtitle={row => <HoverValue tooltip={row.guestTooltip}>{row.email}</HoverValue>}
							width="32%"
						/>
						<Table.TitleSubtitleColumn
							accessorKey="clientName"
							header="Client"
							renderTitle={row => <HoverValue tooltip={row.clientTooltip}>{row.clientName}</HoverValue>}
							renderSubtitle={row => <HoverValue tooltip={row.clientTooltip}>{`ID ${row.clientId}`}</HoverValue>}
							width="22%"
						/>
						<Table.TextColumn accessorKey="guestSince" header="Guest since" width="12%" />
						<Table.TextColumn accessorKey="reason" header="Reason" width="24%" />
					</Table>
				</GuestBox>
			</GuestPanel>
		)}
	</CompanyItem>
);

// The dry run's findings below the totals: a no-work banner, the companies that could not be checked,
// and the companies with contacts to create, each opening onto the guests it would create.
const DryRunDetail = ({result}) => {
	const groups = toCompanyGroups(result.companies);
	const failures = result.failures;
	// The largest company starts open so the operator sees real guests without a click.
	const [openIds, setOpenIds] = useState(() => new Set(groups.length > 0 ? [groups[0].id] : []));
	const toggle = id =>
		setOpenIds(current => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	return (
		<div style={{marginTop: 32, width: 'calc(100vw - 64px)', maxWidth: 1400}} data-cy="backfill-dry-run-detail">
			<FlexColumn gap="l">
				{result.contactsCreated === 0 && (
					<div data-cy="backfill-no-work">
						<Alert type={Alert.TYPE.SUCCESS} text={`No back-fill required. ${noBackfillNote(failures.length)}`} />
					</div>
				)}
				{failures.length > 0 && (
					<div data-cy="backfill-failures">
						<FlexColumn gap="s">
							<Alert
								type={Alert.TYPE.ERROR}
								text={`Failed companies (${failures.length}). These companies errored during the dry run and were not checked. Their contacts are not included in the totals above.`}
							/>
							<Table data={failures} width="100%" getRowId={row => String(row.companyId)}>
								<Table.TitleSubtitleColumn
									accessorKey="companyName"
									header="Company"
									renderTitle={row => row.companyName || ''}
									renderSubtitle={row => companyMeta(row.companyId, row.tier)}
									width="40%"
								/>
								<Table.TextColumn accessorKey="error" header="Error" width="60%" />
							</Table>
						</FlexColumn>
					</div>
				)}
				{groups.length > 0 && (
					<CompaniesPanel data-cy="backfill-companies">
						<PanelHeader>
							<SubHeading>Companies with contacts to create ({groups.length})</SubHeading>
							<Text color="medium">
								{plural(result.contactsCreated, 'contact', 'contacts')} will be created by Run back-fill. Click
								a company to see its guests.
							</Text>
						</PanelHeader>
						<ColumnBar>
							<span />
							<Text color="medium">Company</Text>
							<RightCell>
								<Text color="medium">Contacts to create</Text>
							</RightCell>
						</ColumnBar>
						{groups.map(group => (
							<CompanyRow
								key={group.id}
								group={group}
								open={openIds.has(group.id)}
								onToggle={() => toggle(group.id)}
							/>
						))}
					</CompaniesPanel>
				)}
			</FlexColumn>
		</div>
	);
};

const ClientContactsBackfill = ({viewer}) => {
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState(null);
	const [progress, setProgress] = useState(null);

	// retry=false: a retried mutation starts a second concurrent sweep over the same companies
	// while the first is still running server-side, and client_contact has no unique index on
	// (client_id, email) to absorb the resulting double insert.
	const runChunk = (dryRun, afterCompanyId) =>
		new Promise((resolve, reject) => {
			Util.CommitMutation(
				BackfillClientContacts,
				{dryRun, afterCompanyId, limit: CHUNK_SIZE},
				response => resolve(response.backfillClientContacts),
				false,
				null,
				reject
			);
		});

	const run = async dryRun => {
		if (
			!dryRun &&
			!window.confirm(
				'Run the Contacts back-fill for ALL companies? This creates client_contact rows for existing guest persons. It is idempotent and safe to re-run.'
			)
		) {
			return;
		}
		setRunning(true);
		setResult(null);

		const totals = {
			companiesProcessed: 0,
			companiesFailed: 0,
			contactsCreated: 0,
			companiesWithContactsToCreate: 0,
			companies: [],
			failures: [],
			dryRun,
		};
		let afterCompanyId = 0;

		try {
			// Terminates on an empty chunk. The cursor only moves forward, so a company that
			// yields nothing insertable cannot wedge the walk.
			for (;;) {
				const r = await runChunk(dryRun, afterCompanyId);
				if (!r) {
					setProgress(null);
					break;
				}
				// The server answers an operator outside the Forecast company with an empty payload
				// rather than an error. Adding its nulls up would read as a clean 0 / 0 / 0.
				if (r.companiesProcessed == null) {
					throw NOT_AUTHORIZED;
				}
				// Accumulate before testing for the end, so a chunk's failures always reach the
				// totals the operator is shown.
				totals.companiesProcessed += r.companiesProcessed;
				totals.companiesFailed += r.companiesFailed;
				totals.contactsCreated += r.contactsCreated;
				totals.companiesWithContactsToCreate += r.companiesWithContactsToCreate || 0;
				totals.companies.push(...(r.companies || []));
				totals.failures.push(...(r.failures || []));
				// Empty chunk means done. Test processed + failed rather than processed alone: the
				// server only counts a company as processed when it succeeded, so a chunk whose
				// companies all threw reports 0 processed with the rest of the estate still ahead.
				if (r.companiesProcessed + r.companiesFailed === 0) {
					setProgress(null);
					break;
				}
				afterCompanyId = r.lastCompanyId;
				setProgress({...totals, companiesRemaining: r.companiesRemaining});
			}

			setResult(totals);
			createToast({
				duration: 8000,
				message: `${dryRun ? 'Dry run' : 'Back-fill'} complete — ${totals.companiesProcessed} companies processed, ${
					totals.companiesFailed
				} failed, ${totals.contactsCreated} contacts ${dryRun ? 'would be created' : 'created'}.`,
			});
		} catch (e) {
			if (e === NOT_AUTHORIZED) {
				setResult({dryRun, notAuthorized: true});
				createToast({
					duration: 8000,
					message: `${dryRun ? 'Dry run' : 'Back-fill'} did not run: this user is not in the Forecast company.`,
				});
				return;
			}
			// Nothing to unwind. Contacts are committed per batch inside the company sync, so whatever
			// was written stays written. There is no stored cursor either — a later run rediscovers
			// the outstanding companies, because a company with no remaining guests stops matching.
			setResult({...totals, interrupted: true});
			createToast({
				duration: 8000,
				message: `${dryRun ? 'Dry run' : 'Back-fill'} stopped after ${
					totals.companiesProcessed
				} companies. Contacts already created are saved — run again to continue.`,
			});
		} finally {
			setRunning(false);
			setProgress(null);
		}
	};

	return (
		<>
			<MissionControlHeader viewer={viewer} />
			<div className="mission-control-client-contacts-backfill" style={{padding: 24, maxWidth: 720}}>
				<h2>Contacts GA back-fill</h2>
				<p>
					Creates <code>client_contact</code> rows for existing guest persons across all companies. Reuses the
					per-company sync, so it is idempotent and safe to re-run. Use <strong>Dry run</strong> first to preview the
					count.
				</p>
				<p>
					Runs in chunks of {CHUNK_SIZE} companies. Contacts are written in batches and committed as it goes, so
					nothing is lost if this is interrupted — run it again and it skips the companies already done and continues
					with the rest. Closing this page stops the run; it does not undo anything.
				</p>
				<div style={{display: 'flex', gap: 12, marginTop: 16}}>
					<Button
						size={Button.SIZE.SMALL}
						variant={Button.VARIANT.PURPLE_FILLED}
						disabled={running}
						onClick={() => run(true)}
					>
						Dry run
					</Button>
					<Button
						size={Button.SIZE.SMALL}
						variant={Button.VARIANT.PURPLE_FILLED}
						disabled={running}
						onClick={() => run(false)}
					>
						Run back-fill
					</Button>
				</div>
				{progress && (
					<div style={{marginTop: 24}}>
						<strong>Running…</strong>
						<ul>
							<li>Companies processed so far: {progress.companiesProcessed}</li>
							<li>Companies remaining: {progress.companiesRemaining}</li>
							<li>
								Contacts {progress.dryRun ? 'counted' : 'created'} so far: {progress.contactsCreated}
							</li>
						</ul>
					</div>
				)}
				{result && result.notAuthorized && (
					<div style={{marginTop: 24}}>
						<strong>{result.dryRun ? 'Dry run' : 'Back-fill'} did not run</strong>
						<p>
							The server refused it because this user is not in the Forecast company. No numbers are shown,
							because none were calculated.
						</p>
					</div>
				)}
				{result && !result.notAuthorized && (
					<div style={{marginTop: 24}}>
						<strong>
							{result.dryRun ? 'Dry run' : 'Back-fill'} {result.interrupted ? 'stopped' : 'result'}
						</strong>
						<ul>
							<li>Companies processed: {result.companiesProcessed}</li>
							<li>
								Companies {result.dryRun ? 'with contacts to create' : 'with contacts created'}:{' '}
								{result.companiesWithContactsToCreate}
							</li>
							<li>Companies failed: {result.companiesFailed}</li>
							<li>
								Contacts {result.dryRun ? 'that would be created' : 'created'}: {result.contactsCreated}
							</li>
						</ul>
						{result.companiesProcessed > result.companiesWithContactsToCreate && (
							<p>
								{result.companiesProcessed - result.companiesWithContactsToCreate} of the processed companies
								only have guests that cannot be copied to a contact, so they are selected on every run but have
								nothing to create.
							</p>
						)}
						{result.dryRun && !result.interrupted && <DryRunDetail result={result} />}
						{result.interrupted && (
							<p>
								Contacts already created are saved. Run again to continue with the companies still outstanding.
							</p>
						)}
					</div>
				)}
			</div>
		</>
	);
};

const clientContactsBackfillQuery = graphql`
	query clientContactsBackfill_Query {
		viewer {
			actualPersonId
			component(name: "control_client_contacts_backfill")
			...MissionControlHeader_viewer
		}
	}
`;

export {clientContactsBackfillQuery};

export default withRouter(ClientContactsBackfill);
